import { normalizeAnswer } from './normalizeAnswer.js';
/**
 * Sort into boxes — one category per line: `Category // item1, item2, item3`
 * Box columns = category names (order = first appearance). Items are comma-separated.
 *
 * Also accepts legacy lines `item // BoxName` (no comma on the right) for older bodies.
 *
 * @returns {{ boxes: string[], items: { idx: number, text: string, box: string }[] }}
 */
export function parseSortBoxesBody(body) {
    const items = [];
    const boxOrder = [];
    const seenBoxes = new Set();

    const ensureBox = (box) => {
        if (!box || seenBoxes.has(box)) return;
        seenBoxes.add(box);
        boxOrder.push(box);
    };

    const pushItem = (text, box) => {
        const t = String(text || '').trim();
        if (!t) return;
        ensureBox(box);
        items.push({ idx: items.length, text: t, box: box || '' });
    };

    (body || '').split('\n').forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const sep = trimmed.indexOf('//');
        if (sep === -1) {
            // Bare line: pool item with no box key (ungraded until authored correctly)
            pushItem(trimmed, '');
            return;
        }
        const left = trimmed.substring(0, sep).trim();
        const right = trimmed.substring(sep + 2).trim();
        if (!left) return;

        // New format: Category // a, b, c  (comma on the right, or treat as category with one/more items)
        // Prefer category-first whenever the right side has a comma OR looks like a list.
        if (right.includes(',')) {
            ensureBox(left);
            right.split(',').forEach((part) => pushItem(part, left));
            return;
        }

        // Single item on the right → category // item (new style)
        // Legacy item // BoxName is indistinguishable from category // oneItem;
        // we standardize on category // items (left = box, right = item(s)).
        if (right) {
            ensureBox(left);
            pushItem(right, left);
            return;
        }

        // Category with no items yet — still create the empty box
        ensureBox(left);
    });

    return { boxes: boxOrder, items };
}

export function countSortBoxesItems(body) {
    return parseSortBoxesBody(body).items.length;
}

/**
 * Grade: answers[idx] = box name the student placed the item into.
 */
export function computeSortBoxesGrade(exercise, answers = {}) {
    const { items } = parseSortBoxesBody(exercise?.body || '');
    const is_correct = Object.fromEntries(
        items.map(({ idx, box }) => {
            const studentBox = answers[idx] ?? answers[String(idx)] ?? '';
            const ok = !!box && normalizeAnswer(String(studentBox)) === normalizeAnswer(box);
            return [String(idx), ok];
        }),
    );
    const score = Object.values(is_correct).filter(Boolean).length;
    return { is_correct, score, total: items.length };
}

/**
 * Error correction — prose with `[wrong/correct]` or `[wrong/a/b]` spans.
 * Wrong form (before first `/`) is shown; answers are the segments after `/`.
 * No slash → treat whole bracket as wrong form with no auto-key (teacher mark).
 *
 * @returns {{ segments: Array<
 *   | { type: 'text', text: string }
 *   | { type: 'error', idx: number, wrong: string, answers: string[] }
 * > }
 */
export function parseErrorCorrectionBody(body) {
    const src = String(body || '');
    const segments = [];
    let errorIdx = 0;
    let i = 0;

    while (i < src.length) {
        const open = src.indexOf('[', i);
        if (open === -1) {
            const rest = src.slice(i);
            if (rest) segments.push({ type: 'text', text: rest });
            break;
        }
        if (open > i) {
            segments.push({ type: 'text', text: src.slice(i, open) });
        }
        const close = src.indexOf(']', open + 1);
        if (close === -1) {
            segments.push({ type: 'text', text: src.slice(open) });
            break;
        }
        const inner = src.slice(open + 1, close);
        const bits = inner.split('/');
        if (bits.length === 1) {
            const wrong = bits[0].trim();
            segments.push({
                type: 'error',
                idx: errorIdx++,
                wrong: wrong || '___',
                answers: [],
            });
        } else {
            const wrong = (bits[0] || '').trim() || '___';
            const answers = bits.slice(1).map((a) => a.trim()).filter(Boolean);
            segments.push({
                type: 'error',
                idx: errorIdx++,
                wrong,
                answers,
            });
        }
        i = close + 1;
    }

    return { segments };
}

/**
 * Flatten body into display tokens for click-to-find UX.
 * Plain words are selectable; each `[wrong/…]` span is one selectable unit.
 */
export function buildErrorCorrectionTokens(body) {
    const { segments } = parseErrorCorrectionBody(body);
    const tokens = [];
    let id = 0;

    const pushPlain = (text) => {
        if (!text) return;
        const parts = String(text).split(/(\s+)/);
        for (const part of parts) {
            if (!part) continue;
            if (/^\s+$/.test(part)) {
                tokens.push({ id: id++, type: 'space', text: part });
            } else {
                tokens.push({ id: id++, type: 'word', text: part });
            }
        }
    };

    for (const seg of segments) {
        if (seg.type === 'text') {
            pushPlain(seg.text);
        } else {
            tokens.push({
                id: id++,
                type: 'error',
                text: seg.wrong,
                errorIdx: seg.idx,
                answers: seg.answers || [],
            });
        }
    }
    return tokens;
}

export function collectErrorCorrectionItems(body) {
    return parseErrorCorrectionBody(body).segments.filter((s) => s.type === 'error');
}

export function countErrorCorrectionItems(body) {
    return collectErrorCorrectionItems(body).length;
}

export function computeErrorCorrectionGrade(exercise, answers = {}) {
    const items = collectErrorCorrectionItems(exercise?.body || '');
    const is_correct = Object.fromEntries(
        items.map(({ idx, answers: keys }) => {
            const studentVal = answers[idx] ?? answers[String(idx)] ?? '';
            const ok = keys.length > 0
                && keys.some((a) => normalizeAnswer(a) === normalizeAnswer(String(studentVal || '')));
            return [String(idx), ok];
        }),
    );
    const score = Object.values(is_correct).filter(Boolean).length;
    return { is_correct, score, total: items.length };
}
